import { IDatePicker } from '@common/types/element';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import moment from 'moment';
import connectorInput from '../ConnectorInput';
import createStyles, { errorStyles } from './style';
import { dateConfig, dateTimeConfig } from './helps/config';
import { OPTIONS_MODEL } from './helps/func';
import DateTimePicker from './DateTimePicker';

const DatePickerWeb = require('react-mobile-datepicker');

function useIsSmallScreen(breakpoint = 767) {
  const [isSmall, setIsSmall] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < breakpoint
  );
  useEffect(() => {
    const handler = () => setIsSmall(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isSmall;
}

const DatePicker: FC<IDatePicker> = (props) => {
  const { changeInput, valueInput, defaultDateFormat, data, inputError } =
    props;
  const styles = createStyles(props);
  const isSmallScreen = useIsSmallScreen();
  const isDateTime = defaultDateFormat === 'date';
  const minuteStep = isDateTime ? Number(props.minuteStep) || 1 : 1;

  const [visible, setVisible] = useState(false);

  const defaultValue = useMemo(
    () => valueInput ?? data?.defaultDateValue,
    [valueInput, data?.defaultDateValue]
  );

  const defaultTimeValue = useMemo(() => {
    const currentDate = defaultValue ? new Date(defaultValue) : new Date();
    const snapped =
      Math.round(moment(currentDate).minutes() / minuteStep) * minuteStep;
    return moment(currentDate).minutes(snapped).startOf('minute').toDate();
  }, [defaultValue, minuteStep]);

  const customDateTimeConfig = {
    ...dateTimeConfig,
    minute: { ...dateTimeConfig.minute, step: minuteStep },
  };

  const handleConfirm = (date: any) => {
    changeInput(new Date(date).toISOString());
    setVisible(false);
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    border: 'none',
    background: 'transparent',
    borderRadius: props.style?.borderRadius || props.borderRadius,
    fontSize: props.fontSize,
    color: props.style?.color || props.color,
    cursor: 'pointer',
    outline: 'none',
    fontFamily: 'inherit',
    paddingInline: 8,
  };

  if (isSmallScreen) {
    return (
      <React.Fragment>
        <TouchableOpacity
          activeOpacity={1}
          style={[styles.container, inputError ? errorStyles.errorWrapper : {}]}
          onPress={() => setVisible(true)}
        >
          <Text style={styles.text} numberOfLines={1}>
            {defaultValue
              ? moment(defaultValue).format(OPTIONS_MODEL[defaultDateFormat])
              : ''}
          </Text>
          <DatePickerWeb
            value={defaultTimeValue}
            min={new Date(1900, 0, 0)}
            max={new Date(2500, 0, 0)}
            isOpen={visible}
            onSelect={handleConfirm}
            onCancel={() => setVisible(false)}
            confirmText={'OK'}
            cancelText={'Cancel'}
            theme={'ios'}
            style={{ color: props.color }}
            dateConfig={isDateTime ? customDateTimeConfig : dateConfig}
          />
        </TouchableOpacity>
        {inputError ? (
          <Text style={[errorStyles.errorMessage]}>{inputError}</Text>
        ) : null}
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <View
        style={[styles.container, inputError ? errorStyles.errorWrapper : {}]}
      >
        <DateTimePicker
          mode={isDateTime ? 'datetime' : 'date'}
          changeInput={changeInput}
          valueInput={defaultValue}
          inputStyle={inputStyle}
          minuteStep={minuteStep}
        />
      </View>
      {inputError ? (
        <Text style={[errorStyles.errorMessage]}>{inputError}</Text>
      ) : null}
    </React.Fragment>
  );
};

export default connectorInput(DatePicker);
